import styled from '@emotion/styled';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { LoadingButton } from '@mui/lab';
import {
  DialogTitle,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
} from '@mui/material';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import TextField from '@mui/material/TextField';
import useGetAllBaseRole from 'api/accessControl/useGetAllBaseRole/useGetAllBaseRolle';
import SpinnerLoader from 'components/shared/SpinnerLoader/SpinnerLoader';
import React, { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';

const defaultValues = {
  roleGUID: '',
  mobile: '',
  name: '',
  family: '',
  password: '',
  confirmPassword: '',
};

const UserInsertModal = ({ open, handleClose, insertLoading, onInsert }) => {
  const [getRoleList, rolesLoading, roleList] = useGetAllBaseRole();
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const {
    handleSubmit,
    reset,
    register,
    control,
    watch,
    formState: { errors, isSubmitted },
  } = useForm({ defaultValues });

  const password = watch('password');

  useEffect(() => {
    if (open) {
      reset(defaultValues);
      setShowPassword(false);
      setShowConfirmPassword(false);
      getRoleList();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, reset]);

  const onSubmit = data => {
    onInsert({
      roleGUID: data.roleGUID,
      mobile: data.mobile,
      name: data.name,
      family: data.family,
      password: data.password,
    });
  };

  return (
    <Dialog open={open} onClose={handleClose} sx={{ direction: 'rtl' }}>
      <DialogTitle>اضافه کردن کاربر جدید</DialogTitle>

      <DialogContent>
        <FormFieldWrapper>
          <FormControl variant="standard" sx={{ m: 2, minWidth: 300 }}>
            <TextField
              label="نام"
              variant="standard"
              sx={{ minWidth: 300 }}
              {...register('name', { required: 'نام الزامی است' })}
              error={isSubmitted ? !!errors.name : null}
              helperText={errors.name?.message}
              autoComplete="off"
            />
          </FormControl>
        </FormFieldWrapper>

        <FormFieldWrapper>
          <FormControl variant="standard" sx={{ m: 2, minWidth: 300 }}>
            <TextField
              label="نام خانوادگی"
              variant="standard"
              sx={{ minWidth: 300 }}
              {...register('family', { required: 'نام خانوادگی الزامی است' })}
              error={isSubmitted ? !!errors.family : null}
              helperText={errors.family?.message}
              autoComplete="off"
            />
          </FormControl>
        </FormFieldWrapper>

        <FormFieldWrapper>
          <FormControl variant="standard" sx={{ m: 2, minWidth: 300 }}>
            <TextField
              autoFocus
              label="شماره موبایل"
              variant="standard"
              sx={{ minWidth: 300 }}
              inputProps={{ dir: 'ltr', inputMode: 'numeric', maxLength: 11 }}
              placeholder="09xxxxxxxxx"
              {...register('mobile', {
                required: 'موبایل الزامی است',
              })}
              error={isSubmitted ? !!errors.mobile : null}
              helperText={errors.mobile?.message}
            />
          </FormControl>
        </FormFieldWrapper>

        <FormFieldWrapper>
          <FormControl
            variant="standard"
            sx={{ m: 2, minWidth: 300 }}
            error={isSubmitted ? !!errors.roleGUID : false}
          >
            <InputLabel id="role-select-label">نقش</InputLabel>
            <Controller
              name="roleGUID"
              control={control}
              rules={{ required: 'انتخاب نقش الزامی است' }}
              render={({ field }) => (
                <Select {...field} labelId="role-select-label" label="نقش">
                  {rolesLoading ? (
                    <SpinnerLoader />
                  ) : (
                    roleList.map(role => (
                      <MenuItem
                        value={role.key}
                        key={role.key}
                        sx={{ direction: 'rtl' }}
                      >
                        {role.value}
                      </MenuItem>
                    ))
                  )}
                </Select>
              )}
            />
          </FormControl>
        </FormFieldWrapper>

        <FormFieldWrapper>
          <FormControl variant="standard" sx={{ m: 2, minWidth: 300 }}>
            <TextField
              dir="ltr"
              label="رمز عبور"
              type={showPassword ? 'text' : 'password'}
              variant="standard"
              sx={{ minWidth: 300 }}
              {...register('password')}
              error={isSubmitted ? !!errors.password : null}
              helperText={errors.password?.message}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={() => setShowPassword(prev => !prev)}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              autoComplete="off"
            />
          </FormControl>
        </FormFieldWrapper>

        <FormFieldWrapper>
          <FormControl variant="standard" sx={{ m: 2, minWidth: 300 }}>
            <TextField
              dir="ltr"
              label="تکرار رمز عبور"
              type={showConfirmPassword ? 'text' : 'password'}
              variant="standard"
              sx={{ minWidth: 300 }}
              {...register('confirmPassword', {
                validate: value =>
                  value === password || 'رمز عبور و تکرار آن یکسان نیستند',
              })}
              error={isSubmitted ? !!errors.confirmPassword : null}
              helperText={errors.confirmPassword?.message}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle confirm password visibility"
                      onClick={() => setShowConfirmPassword(prev => !prev)}
                      edge="end"
                    >
                      {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              autoComplete="off"
            />
          </FormControl>
        </FormFieldWrapper>
      </DialogContent>

      <DialogActions sx={{ gap: 1, padding: 3 }}>
        <Button
          onClick={handleClose}
          variant="contained"
          disabled={insertLoading}
        >
          انصراف
        </Button>
        <LoadingButton
          color="warning"
          variant="contained"
          onClick={handleSubmit(onSubmit)}
          loading={insertLoading}
        >
          اضافه کردن
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};

export default UserInsertModal;

const FormFieldWrapper = styled.div`
  margin: 0px 8px 0;
  min-width: 300px;
`;
